import { PlayIcon } from '@heroicons/react/24/solid';
import { Box, Card, CardMedia } from '@mui/material';
import { memo, useEffect, useRef, useState } from 'react';
import { cn } from 'utils/styles';
import { getIcon, getImageProfile } from '../utils/galleryUtils';

function VideoCard({ video, onClick, store }) {
  const model = video.model;
  const [isHovered, setIsHovered] = useState(false);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const videoRef = useRef(null);
  const containerRef = useRef(null);

  const getAspectRatio = () => {
    if (video.generation_params?.ratio) {
      const [width, height] = video.generation_params.ratio
        .split(':')
        .map(Number);
      return (height / width) * 100;
    }
    return 56.25; // 16:9 default
  };

  useEffect(() => {
    const preloadVideo = () => {
      if (videoRef.current) {
        videoRef.current.load();
        videoRef.current.addEventListener('loadeddata', () => {
          setIsVideoLoaded(true);
        });
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            preloadVideo();
            observer.unobserve(entry.target);
          }
        });
      },
      {
        rootMargin: '100px 0px',
        threshold: 0.1,
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, []);

  const handleMouseEnter = () => {
    setIsHovered(true);
    if (videoRef.current && isVideoLoaded) {
      videoRef.current.currentTime = 0;
      videoRef.current.play();
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    if (videoRef.current) {
      videoRef.current.pause();
    }
  };

  return (
    <Card
      ref={containerRef}
      className="scale-100 transition-all duration-300 hover:scale-105"
      sx={{
        boxShadow: 3,
        borderRadius: 3,
        cursor: 'pointer',
        bgcolor: 'background.paper',
        p: 0,
        m: 0,
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={() => onClick(video)}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          paddingTop: `${getAspectRatio()}%`,
        }}
      >
        <CardMedia
          component="img"
          image={
            video.thumbnails?.large ||
            video.thumbnails?.medium ||
            video.thumbnails?.small
          }
          alt={video.type}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transition: 'opacity 0.3s ease-in-out',
            opacity: isHovered ? 0 : 1,
            filter: isVideoLoaded ? 'none' : 'blur(10px)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            transition: 'opacity 0.3s ease-in-out',
            opacity: isHovered ? 0 : 1,
            zIndex: 1,
          }}
        >
          <PlayIcon className="h-16 w-16 text-cool-gray" />
        </Box>
        <CardMedia
          component="video"
          ref={videoRef}
          src={video.low_quality_url || video.url}
          muted
          loop
          playsInline
          preload="auto"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transition: 'opacity 0.3s ease-in-out',
            opacity: isHovered && isVideoLoaded ? 1 : 0,
            filter: isVideoLoaded ? 'none' : 'blur(10px)',
          }}
        />
        <div
          className={cn(
            'w-full absolute p-2 bottom-0 right-0 flex items-center gap-1',
            {
              'bg-gradient-to-t from-umbra via-raisin-black/70 to-transparent':
                store.profile._id !== video.creator._id,
            }
          )}
        >
          <div
            className={`flex items-center gap-1 font-figtree font-bold text-xs text-white uppercase px-[6px] py-[3px] bg-${
              model.color ? `[${model.color}]` : 'nue-blue'
            } rounded-lg`}
          >
            {getIcon(model)}
            <div>{model.abbreviation}</div>
          </div>
          {store.profile._id !== video.creator._id && getImageProfile(video)}
        </div>
      </Box>
    </Card>
  );
}
export default memo(VideoCard, (prevProps, nextProps) => {
  return prevProps.video._id === nextProps.video._id;
});
